import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { getIdToken } from '@/services/msal';
import { getApiBaseUrl, getApiHeaders } from './headers';
import { authApi } from './auth';
import { customerApi } from './customer';
import { caseApi } from './case';
import { agentEventsApi } from './agentEvents';
import { managerEventsApi } from './managerEvents';
import { agentsApi } from './agents';
import { routingProfilesApi } from './routingProfiles';
import { callHistoryApi } from './callHistory';
import { callApi } from './call';
import { quickScriptsApi } from './quickScripts';
import { dynamicSuggestionApi } from './dynamicSuggestion';
// import { logout } from '@/store/slices/authSlice';

// Create a dynamic baseQuery that evaluates the URL at query time
// This ensures loadEnvConfig() has completed before the URL is determined
const createBaseQuery = () => {
  const baseUrl = getApiBaseUrl();
  console.log('🔗 API Base URL:', baseUrl);

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: async headers => {
      const apiHeaders = await getApiHeaders();
      Object.entries(apiHeaders).forEach(([key, value]) =>
        headers.set(key, value)
      );
      return headers;
    },
  });
};

// Lazy initialization: baseQuery is created when first accessed
let baseQueryInstance: ReturnType<typeof createBaseQuery> | null = null;
const getBaseQuery = () => {
  if (!baseQueryInstance) {
    baseQueryInstance = createBaseQuery();
  }
  return baseQueryInstance;
};

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseQuery = getBaseQuery();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const { STORAGE_KEYS } = await import('@/constants');
    const currentToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const { token: newIdToken } = await getIdToken(currentToken);

    if (newIdToken && newIdToken !== currentToken) {
      // Token was refreshed, retry the request
      result = await baseQuery(args, api, extraOptions);

      if (result.error && result.error.status === 401) {
        // api.dispatch(logout());
        console.log('new token, but still 401, skipping logout');
      }
    } else {
      console.log('no new token, skipping logout');
      // api.dispatch(logout());
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'Auth',
    'AgentEvents',
    'Agents',
    'RoutingProfiles',
    'Customer',
    'Call',
    'Case',
    'QualityMetrics',
    'Call-Transcript',
    'QuickScripts',
    'DynamicSuggestion',
  ],
  endpoints: builder => ({
    ...authApi(builder),
    ...agentEventsApi(builder),
    ...managerEventsApi(builder),
    ...agentsApi(builder),
    ...routingProfilesApi(builder),
    ...customerApi(builder),
    ...callHistoryApi(builder),
    ...caseApi(builder),
    ...callApi(builder),
    ...quickScriptsApi(builder),
    ...dynamicSuggestionApi(builder),
  }),
});
